// Journey-Stage Adaptive Dashboards — TPS Home (servicer portfolio dashboard).
//
// Engine-native since the F-09 remediation. The PRIMARY fetch is the readiness
// read ALONE (stage + spine + hero gating) via PortalHomeLayout; the three
// WORK-QUEUE reads — bills, the READ-ONLY distributions summary (F-08/F-31,
// never the lazy-materializing list), loans — are hoisted to the tab level as
// independent `useWidgetData` fetches whose state is shared between the
// NextBestActionCard and their widgets (one fetch each, two consumers). The
// DCL list is hoisted too: the NBA's F-32 missing-disclosure warning and the
// F-33 coverage tile share it. Everything else (collections, commissions, tax
// forms) is widget-owned inside its own WidgetFrame. One widget failing
// degrades ONLY its own card — the spine + hero always render. Portal rules:
// no React Query; navigation uses the shell's ?tab= param (F-34: every stat
// tile deep-links its own tab).

import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { CreditCard, ShieldCheck } from "lucide-react";
import { Button, EmptyState } from "@/design-system/components/ui";
import { formatCurrency } from "@/design-system/lib/format";
import {
  DashboardCard,
  DashboardGrid,
  JourneySpine,
  NextBestActionCard,
  StatTile,
  type WidgetTone,
} from "../dashboard/DashboardPrimitives";
import {
  PortalHomeLayout,
  useWidgetData,
  WidgetFrame,
  type WidgetData,
} from "../dashboard/engine";
import { asTpsStage, TPS_SPINE, tpsSpine, type TpsStage } from "../dashboard/journey";
import { tpsLoansApi, type TpsLoanSummary, type TpsReadiness } from "../api/tpsLoans";
import { tpsBillsApi, type TpsBill } from "../api/tpsBills";
import { tpsDistributionsApi, type TpsDistributionsSummary } from "../api/tpsDistributions";
import { tpsCollectionsApi } from "../api/tpsCollections";
import { tpsCommissionsApi } from "../api/tpsCommissions";
import { tpsTaxFormsApi } from "../api/tpsTaxForms";
import {
  tpsDebtCollectorLanguageApi,
  type TpsDebtCollectorLanguage,
} from "../api/tpsDebtCollectorLanguage";

// ── Stage resolution ─────────────────────────────────────────────────────────

/** Fallback derivation from the readiness booleans — used ONLY when the server
 *  sends a stage string this bundle doesn't recognize (a newer backend must
 *  never crash an older bundle's Home). The server-classified stage is
 *  preferred via `asTpsStage`. Mirrors the C# `PortalJourneyStages.Tps.Classify`. */
function classifyStageFallback(r: TpsReadiness): TpsStage {
  if (!r.isCertified) return "pre-certification";
  if (!r.stripeOnboarded) return "certified-idle";
  if (!r.hasAcceptedLoans) return "stripe-onboarded";
  return "servicing";
}

function resolveStage(r: TpsReadiness): TpsStage {
  return asTpsStage(r.stage) ?? classifyStageFallback(r);
}

function plural(n: number): string {
  return n === 1 ? "" : "s";
}

type GoToTab = (tab: string) => void;

// ── Main tab ─────────────────────────────────────────────────────────────────

export function TpsHomeTab() {
  const [, setParams] = useSearchParams();

  // Switch the portal to another tab (the TpsLoansTab deep-link precedent).
  const goToTab = useCallback<GoToTab>(
    (tab) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("tab", tab);
          return next;
        },
        { replace: false },
      );
    },
    [setParams],
  );

  // PRIMARY fetch — the only read allowed to gate the whole page.
  const readiness = useWidgetData<TpsReadiness>(() => tpsLoansApi.readiness(), []);

  // Work-queue fetches, hoisted to the tab level: each is shared between the
  // NextBestActionCard and its widget (one fetch, two consumers). All are
  // render-safe empties for pre-servicing firms, so unconditional mounting is
  // cheap and keeps the hooks order stable.
  const bills = useWidgetData<TpsBill[]>(() => tpsBillsApi.list(), []);
  const distSummary = useWidgetData<TpsDistributionsSummary>(
    () => tpsDistributionsApi.summary(),
    [],
  );
  const loans = useWidgetData<TpsLoanSummary[]>(() => tpsLoansApi.list(), []);
  // Shared by the NBA's F-32 disclosure warning + the F-33 coverage tile.
  const dcls = useWidgetData<TpsDebtCollectorLanguage[]>(
    () => tpsDebtCollectorLanguageApi.list(),
    [],
  );

  const stage = readiness.data ? resolveStage(readiness.data) : null;
  const queues: WorkQueues = { bills, distSummary, loans, dcls };

  return (
    <PortalHomeLayout
      loading={readiness.status === "loading" && !readiness.data}
      error={readiness.status === "error" ? readiness.error : null}
      onRetry={readiness.reload}
      stage={stage ?? undefined}
      testId="tps-home"
    >
      {stage && (
        <>
          <JourneySpine
            steps={TPS_SPINE}
            currentIndex={tpsSpine(stage).index}
            complete={tpsSpine(stage).complete}
            testId="tps-home-spine"
          />
          <HomeNextAction stage={stage} queues={queues} goToTab={goToTab} />
          <HomeBody stage={stage} queues={queues} goToTab={goToTab} />
        </>
      )}
    </PortalHomeLayout>
  );
}

interface WorkQueues {
  bills: WidgetData<TpsBill[]>;
  distSummary: WidgetData<TpsDistributionsSummary>;
  loans: WidgetData<TpsLoanSummary[]>;
  dcls: WidgetData<TpsDebtCollectorLanguage[]>;
}

// ── Next best action ─────────────────────────────────────────────────────────

interface NextActionSpec {
  heading: string;
  description?: string;
  tone: WidgetTone;
  primary?: { label: string; tab: string };
  secondary?: { label: string; tab: string };
}

function pendingLoans(loans: TpsLoanSummary[] | null): TpsLoanSummary[] {
  return (loans ?? []).filter((l) => l.acceptanceStatus === "Pending");
}

/** F-32 — the accept-loans action, DCL-aware: when a pending loan's borrower
 *  state lacks an Approved debt-collector disclosure, the description names
 *  the state(s) needing one first and a secondary button deep-links the DCL
 *  tab — never a silent steer into the accept gate's guaranteed 400. */
function acceptLoansSpec(
  pending: TpsLoanSummary[],
  dcls: WidgetData<TpsDebtCollectorLanguage[]>,
  heading: string,
): NextActionSpec {
  const n = pending.length;
  const approved = new Set(
    (dcls.data ?? [])
      .filter((d) => d.isApproved)
      .map((d) => d.stateCode.toUpperCase()),
  );
  // Only warn from a loaded DCL list — with the list unavailable we can't know
  // coverage, so the plain accept prompt stands (the accept flow still guards).
  const missingStates =
    dcls.data == null
      ? []
      : [
          ...new Set(
            pending
              .map((l) => l.borrowerState)
              .filter((s): s is string => !!s)
              .map((s) => s.toUpperCase()),
          ),
        ]
          .filter((s) => !approved.has(s))
          .sort();
  const unknownCount = pending.filter((l) => !l.borrowerState).length;

  let description = `You have ${n} loan${plural(n)} awaiting your acceptance. Review the terms and accept to begin servicing.`;
  let secondary: NextActionSpec["secondary"];
  if (missingStates.length > 0) {
    description =
      `You have ${n} loan${plural(n)} awaiting acceptance, but accepting is blocked until you approve ` +
      `your debt-collector disclosure for ${missingStates.join(", ")}. Approve the disclosure${plural(missingStates.length)} first.`;
    secondary = { label: "Review state disclosures", tab: "debt-collector-language" };
  }
  if (unknownCount > 0) {
    description += ` ${unknownCount} loan${plural(unknownCount)} ${unknownCount === 1 ? "has" : "have"} no borrower state on file — contact Phoenix before accepting ${unknownCount === 1 ? "it" : "them"}.`;
  }

  return {
    heading,
    description,
    tone: "warning",
    primary: { label: "Review assigned loans", tab: "loans" },
    secondary,
  };
}

function nextAction(stage: TpsStage, q: WorkQueues): NextActionSpec {
  switch (stage) {
    case "pre-certification":
      return {
        heading: "Get certified to start servicing",
        description:
          "Phoenix certifies your firm before assigning loans. Once you're certified, payout setup and your first assignments unlock here.",
        tone: "default",
      };
    case "certified-idle":
      // F-07 — a real action, not a dead end: the self-serve Payment setup tab
      // can mint a fresh Stripe onboarding link on demand.
      return {
        heading: "Finish Stripe onboarding so funds can flow",
        description:
          "You're certified. Open Payment setup to generate a fresh Stripe onboarding link yourself and complete Connect onboarding, so you can receive borrower payments and pay out distributions.",
        tone: "warning",
        primary: { label: "Set up payouts", tab: "payment-setup" },
      };
    case "stripe-onboarded": {
      if (q.loans.data) {
        const pending = pendingLoans(q.loans.data);
        if (pending.length > 0)
          return acceptLoansSpec(pending, q.dcls, `Accept your assigned loan${plural(pending.length)}`);
        return {
          heading: "You're onboarded — no loans assigned yet",
          description:
            "Phoenix will assign loans for you to service. New assignments show up here for you to review and accept.",
          tone: "success",
        };
      }
      if (q.loans.status === "error") return notAllLoaded();
      return checking();
    }
    case "servicing": {
      // Priority chain over whichever work queues are READY; a failed queue
      // never silently reads as empty — see the not-all-loaded arm below.
      const billsPending =
        q.bills.data?.filter((b) => b.status === "TpsApprovalPending").length ?? 0;
      if (q.bills.data && billsPending > 0) {
        return {
          heading: `${billsPending} bill${plural(billsPending)} awaiting your approval`,
          description: "Review and approve the monthly bills Phoenix prepared on your behalf.",
          tone: "warning",
          primary: { label: "Review bills", tab: "bills" },
        };
      }
      const s = q.distSummary.data;
      if (s && s.blockedCount > 0) {
        return {
          heading: `${s.blockedCount} distribution${plural(s.blockedCount)} blocked`,
          description:
            "A payout can't go out yet — usually funds that haven't cleared the settlement cushion or a payee that isn't onboarded. Open the ledger to see why.",
          tone: "danger",
          primary: { label: "Review distributions", tab: "distributions" },
        };
      }
      if (s && s.approvableCount > 0) {
        return {
          heading: `${s.approvableCount} distribution${plural(s.approvableCount)} ready to approve`,
          description: "Approve to pay out each cleared payment to its recipients.",
          tone: "default",
          primary: { label: "Approve distributions", tab: "distributions" },
        };
      }
      const pending = pendingLoans(q.loans.data);
      if (q.loans.data && pending.length > 0)
        return acceptLoansSpec(pending, q.dcls, `Accept your new loan${plural(pending.length)}`);

      // Nothing actionable in the loaded queues — but a failed queue means we
      // must NOT claim all-clear (its work is unknown, not absent).
      const anyFailed = [q.bills, q.distSummary, q.loans].some(
        (w) => w.status === "error",
      );
      if (anyFailed) return notAllLoaded();
      const anyLoading = [q.bills, q.distSummary, q.loans].some(
        (w) => w.status === "loading" && w.data == null,
      );
      if (anyLoading) return checking();
      return {
        heading: "You're all caught up",
        description: "No bills or distributions need your attention right now. Nice work.",
        tone: "success",
      };
    }
  }
}

function notAllLoaded(): NextActionSpec {
  return {
    heading: "Some of your work queues couldn't load",
    description:
      "We can't confirm you're caught up — retry the affected card below to see what needs your attention.",
    tone: "default",
  };
}

function checking(): NextActionSpec {
  return { heading: "Checking your work queues…", tone: "default" };
}

function HomeNextAction({
  stage,
  queues,
  goToTab,
}: {
  stage: TpsStage;
  queues: WorkQueues;
  goToTab: GoToTab;
}) {
  const action = nextAction(stage, queues);
  return (
    <NextBestActionCard
      heading={action.heading}
      description={action.description}
      tone={action.tone}
      testId="tps-home-next-action"
      actions={
        action.primary || action.secondary ? (
          <>
            {action.primary && (
              <Button
                type="button"
                variant="primary"
                size="sm"
                onClick={() => goToTab(action.primary!.tab)}
                data-testid="tps-home-next-action-cta"
              >
                {action.primary.label}
              </Button>
            )}
            {action.secondary && (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => goToTab(action.secondary!.tab)}
                data-testid="tps-home-next-action-secondary"
              >
                {action.secondary.label}
              </Button>
            )}
          </>
        ) : undefined
      }
    />
  );
}

// ── Stage bodies ─────────────────────────────────────────────────────────────

function HomeBody({
  stage,
  queues,
  goToTab,
}: {
  stage: TpsStage;
  queues: WorkQueues;
  goToTab: GoToTab;
}) {
  // Pre-servicing stages have no portfolio yet — teach what unlocks it.
  if (stage === "pre-certification") {
    return (
      <DashboardCard testId="tps-home-portfolio">
        <EmptyState
          icon={ShieldCheck}
          title="Certification in progress"
          description="Once Phoenix certifies your firm, you'll set up payouts and start receiving loan assignments. Your portfolio dashboard fills in here."
        />
      </DashboardCard>
    );
  }
  if (stage === "certified-idle") {
    return (
      <DashboardCard testId="tps-home-portfolio">
        <EmptyState
          icon={CreditCard}
          title="One step from servicing"
          description="Finish Stripe onboarding from the Payment setup tab — you can generate a fresh onboarding link there yourself — to unlock loan assignments and start billing. Your portfolio fills in here as soon as funds can flow."
        />
      </DashboardCard>
    );
  }

  // Stripe-onboarded but nothing accepted yet — a loans-focused mini grid.
  if (stage === "stripe-onboarded") {
    return (
      <DashboardGrid data-testid="tps-home-dashboard">
        <WidgetFrame title="Loans" state={queues.loans} testId="tps-home-widget-loans">
          <div className="grid grid-cols-2 gap-space-3">
            <StatTile
              label="Loans assigned"
              value={queues.loans.data?.length ?? 0}
              hint={
                (queues.loans.data?.length ?? 0) === 0
                  ? "None yet"
                  : `${queues.loans.data!.filter((l) => l.acceptanceStatus === "Accepted").length} accepted`
              }
              onClick={() => goToTab("loans")}
              testId="tps-home-tile-loans"
            />
            <StatTile
              label="Awaiting acceptance"
              value={pendingLoans(queues.loans.data).length}
              tone={pendingLoans(queues.loans.data).length > 0 ? "warning" : "default"}
              onClick={() => goToTab("loans")}
              testId="tps-home-tile-pending"
            />
          </div>
        </WidgetFrame>
        <DclCoverageWidget loans={queues.loans} dcls={queues.dcls} goToTab={goToTab} />
      </DashboardGrid>
    );
  }

  // Servicing — the full operational summary, one independently-degradable
  // widget per data source (F-09).
  return (
    <DashboardGrid data-testid="tps-home-dashboard">
      <LoansWidget loans={queues.loans} goToTab={goToTab} />
      <BillsWidget bills={queues.bills} goToTab={goToTab} />
      <DistributionsWidget summary={queues.distSummary} goToTab={goToTab} />
      <CommissionsWidget goToTab={goToTab} />
      <CollectionsWidget goToTab={goToTab} />
      <TaxFormsWidget goToTab={goToTab} />
      <DclCoverageWidget loans={queues.loans} dcls={queues.dcls} goToTab={goToTab} />
    </DashboardGrid>
  );
}

// ── Widgets over the shared work-queue fetches ───────────────────────────────

function LoansWidget({ loans, goToTab }: { loans: WidgetData<TpsLoanSummary[]>; goToTab: GoToTab }) {
  const all = loans.data ?? [];
  const accepted = all.filter((l) => l.acceptanceStatus === "Accepted").length;
  const pending = all.filter((l) => l.acceptanceStatus === "Pending").length;
  const declined = all.filter((l) => l.acceptanceStatus === "Declined").length;
  return (
    <WidgetFrame title="Loans" state={loans} testId="tps-home-widget-loans">
      <StatTile
        label="Loans serviced"
        value={accepted}
        hint={`${pending} pending · ${declined} declined`}
        onClick={() => goToTab("loans")}
        testId="tps-home-tile-loans"
      />
    </WidgetFrame>
  );
}

function BillsWidget({ bills, goToTab }: { bills: WidgetData<TpsBill[]>; goToTab: GoToTab }) {
  const pendingApproval =
    bills.data?.filter((b) => b.status === "TpsApprovalPending").length ?? 0;
  return (
    <WidgetFrame title="Bills" state={bills} testId="tps-home-widget-bills">
      <StatTile
        label="Bills to approve"
        value={pendingApproval}
        tone={pendingApproval > 0 ? "warning" : "default"}
        onClick={() => goToTab("bills")}
        testId="tps-home-tile-bills"
      />
    </WidgetFrame>
  );
}

function DistributionsWidget({
  summary,
  goToTab,
}: {
  summary: WidgetData<TpsDistributionsSummary>;
  goToTab: GoToTab;
}) {
  // F-08 — both counts come from the server's REAL approvability math (the
  // same cleared-funds gate the approve path runs), via the F-31 read-only
  // summary endpoint; never "pending minus hasBlockReason".
  const s = summary.data;
  return (
    <WidgetFrame title="Distributions" state={summary} testId="tps-home-widget-distributions">
      <div className="grid grid-cols-2 gap-space-3">
        <StatTile
          label="Ready to approve"
          value={s?.approvableCount ?? 0}
          tone={(s?.approvableCount ?? 0) > 0 ? "warning" : "default"}
          onClick={() => goToTab("distributions")}
          testId="tps-home-tile-distributions"
        />
        <StatTile
          label="Blocked"
          value={s?.blockedCount ?? 0}
          tone={(s?.blockedCount ?? 0) > 0 ? "danger" : "default"}
          onClick={() => goToTab("distributions")}
          testId="tps-home-tile-distributions-blocked"
        />
      </div>
    </WidgetFrame>
  );
}

/** F-33 — TRUE disclosure coverage: approved states with loans over the
 *  distinct borrower states the firm actually services (never authored-row
 *  counts); loans whose state can't be resolved are excluded from the
 *  denominator and surfaced honestly in the hint. */
function DclCoverageWidget({
  loans,
  dcls,
  goToTab,
}: {
  loans: WidgetData<TpsLoanSummary[]>;
  dcls: WidgetData<TpsDebtCollectorLanguage[]>;
  goToTab: GoToTab;
}) {
  const state = combineStates(dcls, loans);
  const serviced = (loans.data ?? []).filter((l) => l.acceptanceStatus !== "Declined");
  const states = [
    ...new Set(
      serviced
        .map((l) => l.borrowerState)
        .filter((s): s is string => !!s)
        .map((s) => s.toUpperCase()),
    ),
  ];
  const unknownCount = serviced.filter((l) => !l.borrowerState).length;
  const approved = new Set(
    (dcls.data ?? []).filter((d) => d.isApproved).map((d) => d.stateCode.toUpperCase()),
  );
  const covered = states.filter((s) => approved.has(s)).length;

  const unknownHint = unknownCount > 0 ? ` · +${unknownCount} unknown state` : "";
  const hint =
    states.length === 0
      ? serviced.length === 0
        ? "No loans assigned yet"
        : `No resolvable borrower states${unknownHint}`
      : `states covered${unknownHint}`;

  return (
    <WidgetFrame title="State disclosures" state={state} testId="tps-home-widget-dcl">
      <StatTile
        label="DCL coverage"
        value={`${covered}/${states.length}`}
        hint={hint}
        tone={covered < states.length || unknownCount > 0 ? "warning" : "default"}
        onClick={() => goToTab("debt-collector-language")}
        testId="tps-home-tile-dcl"
      />
    </WidgetFrame>
  );
}

// ── Widget-owned fetches (each degrades alone) ───────────────────────────────

function CommissionsWidget({ goToTab }: { goToTab: GoToTab }) {
  const state = useWidgetData(() => tpsCommissionsApi.list(), []);
  const total = state.data?.totalEarned ?? 0;
  return (
    <WidgetFrame title="Commissions" state={state} testId="tps-home-widget-commissions">
      <StatTile
        label="Commissions earned"
        value={formatCurrency(total)}
        tone={total > 0 ? "success" : "default"}
        onClick={() => goToTab("commissions")}
        testId="tps-home-tile-commissions"
      />
    </WidgetFrame>
  );
}

function CollectionsWidget({ goToTab }: { goToTab: GoToTab }) {
  const state = useWidgetData(() => tpsCollectionsApi.list(), []);
  const open =
    state.data?.filter((c) => !c.status.toLowerCase().startsWith("closed")).length ?? 0;
  return (
    <WidgetFrame title="Collections" state={state} testId="tps-home-widget-collections">
      <StatTile
        label="Open collections"
        value={open}
        tone={open > 0 ? "warning" : "default"}
        onClick={() => goToTab("collections")}
        testId="tps-home-tile-collections"
      />
    </WidgetFrame>
  );
}

function TaxFormsWidget({ goToTab }: { goToTab: GoToTab }) {
  const state = useWidgetData(() => tpsTaxFormsApi.list(), []);
  const total = state.data?.length ?? 0;
  const drafts = state.data?.filter((f) => f.status === "Draft").length ?? 0;
  return (
    <WidgetFrame title="Tax forms" state={state} testId="tps-home-widget-tax-forms">
      <StatTile
        label="1099 forms"
        value={total}
        hint={drafts > 0 ? `${drafts} draft${plural(drafts)} to review` : "All filed"}
        tone={drafts > 0 ? "warning" : "default"}
        onClick={() => goToTab("tax-forms")}
        testId="tps-home-tile-tax-forms"
      />
    </WidgetFrame>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────────

/** Combines two shared fetches for one WidgetFrame: an error in EITHER
 *  degrades the card (retrying the failed one); loading until both have data. */
function combineStates(
  ...states: WidgetData<unknown>[]
): Pick<WidgetData<unknown>, "status" | "error" | "reload"> {
  const failed = states.find((s) => s.status === "error");
  if (failed) return { status: "error", error: failed.error, reload: failed.reload };
  if (states.some((s) => s.status === "loading" && s.data == null)) {
    return { status: "loading", error: null, reload: states[0].reload };
  }
  return { status: "ready", error: null, reload: states[0].reload };
}
